import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useUser from "@/hooks/useUser";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { Chip } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useUseCases } from "@/context/UseCasesProvider";

interface AssignmentProps {
  currentAssignementId: string | null;
  orderId: string;
  entityId?: string | null;
}

interface User {
  user_id: string;
  user_name: string;
  user_given_name: string;
  user_group: string;
  entities: string;
  allow_update_assignees:boolean;
}

interface UserData {
  user_id: string;
  user_group: string;
  entities: string;
  allow_update_assignees: boolean;
}

interface UserOption {
  label: string;
  value: string;
  fullName: string;
}

interface FormValues {
  assignement: UserOption | null;
}

interface UsersResponse {
  data: User[];
}

export default function Assignment({ currentAssignementId, orderId, entityId }: Readonly<AssignmentProps>) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const settingsInterface = useUseCases();

  const { data: usersData, isLoading: isUsersLoading } = useQuery<UsersResponse>({
    queryKey: ["AllUsers"],
    enabled: false,
  });

  const { data: userData, isLoading: isUserLoading } = useUser();

  const [isAllowUpdateAssignees, setIsAllowUpdateAssignees] = useState(false);
  
    useEffect(() => {
      if (userData) {
        setIsAllowUpdateAssignees(userData.allow_update_assignees);
      }
    }, [userData, isUserLoading]);

  const truncateName = (name: string): string => {
    return name.length > 3 ? name.substring(0, 3) + "." : name;
  };

  const userOptions = useMemo<UserOption[]>(
    () => {
      if (!usersData || !Array.isArray(usersData.data)) {
        return [];
      }
  
      return usersData.data
        .filter((user) => {
          const userGroups = ["Admins", "DataEntryOperators", "DataEntrySupervisors"];
          const hasCorrectGroup = userGroups.some(group => user.user_group?.includes(group) ?? false);
          
          if (!settingsInterface?.settings?.display?.features?.entity) {
            return hasCorrectGroup;
          } else {
            if (entityId) {
              const userEntities = (user.entities ?? '').split(',').map(id => id.trim()).filter(id => id !== '');
              const hasAccessToEntity = userEntities.includes(entityId);
              return hasCorrectGroup && hasAccessToEntity;
            } else {
              return hasCorrectGroup;
            }
          }
        })
        .map((user) => ({
          label: user.user_given_name + " " + truncateName(user.user_name),
          value: user.user_id,
          fullName: user.user_given_name + " " + user.user_name,
        }));
    },
    [usersData, entityId, settingsInterface?.settings?.display?.features?.entity]
  );

  const { control, handleSubmit, setValue } = useForm<FormValues>();

  const [assignmentLabel, setAssignmentLabel] = useState<string>("");

  useEffect(() => {
    if (currentAssignementId && userOptions.length > 0) {
      const defaultOption = userOptions.find((option) => option.value === currentAssignementId);
      setAssignmentLabel(defaultOption ? defaultOption.label : t("not_assigned"));
      setValue("assignement", defaultOption ?? null);
    } else if (currentAssignementId && userOptions.length === 0) {
      setAssignmentLabel(t("not_assigned"));
      setValue("assignement", null);
    }
  }, [currentAssignementId, userOptions, setValue, t]);

  const onSubmit = async (data: FormValues) => {
    if (!data.assignement) return;
    
    try {
      const orderData = {
        assignment_name: data.assignement.fullName,
        assignment_id: data.assignement.value,
      };

      await axiosPrivate.put(`orders/${orderId}/assignment`, orderData);

      const newAssignmentName = data.assignement.fullName;
      const newAssignmentId = data.assignement.value;
      updateOrderAssignment(orderId, newAssignmentName, newAssignmentId);
    } catch (error) {
      console.error(t("error_fetching_updating_order_data"), error);
    }
  };

  const handleSelectChange = (selectedOption: UserOption | null) => {
    setValue("assignement", selectedOption);
    handleSubmit(onSubmit)();
  };

  const updateOrderAssignment = (
    orderId: string,
    newAssignmentName: string,
    newAssignmentId: string,
  ) => {
    queryClient.setQueryData(["orders"], (oldData: any) => {
      if (!oldData?.data) {
        return oldData;
      }
      return {
        ...oldData,
        data: oldData.data.map((order: any) => {
          if (order.process?.jobID === orderId) {
            return {
              ...order,
              process: {
                ...order.process,
                assignment: {
                  ...order.process.assignment,
                  assignment_name: newAssignmentName,
                  assignment_id: newAssignmentId,
                },
              },
            };
          }
          return order;
        }),
      };
    });
  };

  const controlStyles = {
    base: "border-0 rounded-full bg-default-200 hover:cursor-pointer text-xs",
    focus: "border-primary-600 ring-1 ring-primary-500",
    nonFocus: "border-gray-300 hover:border-gray-400",
  };
  const placeholderStyles = "text-gray-500 pl-1 py-0.5";
  const selectInputStyles = "pl-1 py-0.5 text-sm";
  const valueContainerStyles = "px-2 gap-1";
  const singleValueStyles = "leading-5 ml-1 ";
  const indicatorsContainerStyles = "p-1 gap-1";
  const indicatorSeparatorStyles = "bg-gray-300";
  const dropdownIndicatorStyles =
    "p-1 hover:bg-gray-100 text-gray-500 rounded-full hover:text-black";
  const menuStyles = "p-1 mt-2 border border-gray-200 bg-white rounded-lg text-sm";
  const groupHeadingStyles = "ml-3 mt-2 mb-1 text-gray-500";
  const optionStyles = {
    base: "hover:cursor-pointer px-3 py-2 rounded z-99",
    focus: "bg-gray-100 active:bg-gray-200",
    selected: "after:content-['✔'] after:ml-2 after:text-green-500 text-gray-500",
  };
  const noOptionsMessageStyles =
    "text-gray-500 p-2 bg-gray-50 border border-dashed border-gray-200 rounded-sm";
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "30px",
      height: "30px",
      width: "150px",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: "30px",
    }),
  };

  if (isUserLoading) {
    return <div></div>;
  }
  if (!isAllowUpdateAssignees) {
    return (
      <Chip className="capitalize border-none gap-1 text-bold text-tiny text-default-500">
        {assignmentLabel ?? t("not_assigned")}
      </Chip>
    );
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="assignement"
        control={control}
        render={({ field }) => (
          <Select<UserOption>
            {...field}
            unstyled
            styles={customStyles}
            classNames={{
              control: () => controlStyles.base,
              placeholder: () => placeholderStyles,
              input: () => selectInputStyles,
              valueContainer: () => valueContainerStyles,
              singleValue: () => singleValueStyles,
              indicatorsContainer: () => indicatorsContainerStyles,
              indicatorSeparator: () => indicatorSeparatorStyles,
              dropdownIndicator: () => dropdownIndicatorStyles,
              menu: () => menuStyles,
              groupHeading: () => groupHeadingStyles,
              option: () => optionStyles.base,
              noOptionsMessage: () => noOptionsMessageStyles,
            }}
            options={isUsersLoading ? [] : userOptions}
            onChange={handleSelectChange}
            noOptionsMessage={() => t("no_users_available")}
          />
        )}
      />
    </form>
  );
}
